import { useContext, useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";

import T from "./translations/Translations";

import { formatDate } from "../utils/DateFormatter";
import { GlobalContext } from "./contextProvider/ContextProvider";

import "../styles/WelcomeText.scss";

/**
 * For external guardians, determine what instructions to display based on the appropriate OE windows
 * @name WelcomeText
 * @returns {JSX.Element}
 */
const WelcomeText = () => {
    const { state } = useContext(GlobalContext);
    const { oeActionControls, oeDistrictWindows } = state || {};

    const [intentToReturn, setIntentToReturn] = useState(null);

    const [parentOffer, setParentOffer] = useState(null);
    const [parentRequest, setParentRequest] = useState(null);
    const [parentRequestRound2, setParentRequestRound2] = useState(null);

    ///////////////////////////////////////////////////////////////////////////
    // Translations
    // declared here to prevent rule of hooks violations
    //-------------------------------------------------------------------------
    const welcomeTagline = T({ key: "Welcome_Tagline" });
    const welcomeP1 = T({ key: "Welcome_p1" });
    const welcomeP2 = T({ key: "Welcome_p2" });
    const welcomeH51 = T({ key: "Welcome_h5_1" });
    const welcomeImportantDates = T({ key: "Welcome_ImportantDates" });
    const welcomeFirstWindow = T({ key: "Welcome_FirstWindow" });
    const welcomeSecondWindow = T({ key: "Welcome_SecondWindow" });
    /**
     * Get specific OE action control windows
     */
    useEffect(() => {
        if (!oeActionControls || !oeDistrictWindows) {
            return;
        }

        const getWindows = () => {
            try {
                const intent = oeActionControls.filter((obj) => obj.action === "INTENT_TO_RETURN_FORM");
                const round1ActionControls = oeActionControls.filter(
                    (obj) => parseInt(obj.differentiator, 10) === parseInt(oeDistrictWindows[0].key, 10)
                );
                const round2ActionControls = oeActionControls.filter(
                    (obj) => parseInt(obj.differentiator, 10) === parseInt(oeDistrictWindows[1].key, 10)
                );
                if (round1ActionControls && round1ActionControls.length > 0) {
                    const parentOfferMatch = round1ActionControls.filter((obj) => obj.action === "PARENT_OFFER");
                    const parentRequestMatch = round1ActionControls.filter((obj) => obj.action === "PARENT_REQUEST");
                    if (parentOfferMatch && parentOfferMatch.length > 0) {
                        setParentOffer(parentOfferMatch[0]);
                    }
                    if (parentRequestMatch && parentRequestMatch.length > 0) {
                        setParentRequest(parentRequestMatch[0]);
                    }
                }
                if (round2ActionControls && round2ActionControls.length > 0) {
                    setParentRequestRound2(round2ActionControls.find((obj) => obj.action === "PARENT_REQUEST"));
                }
                if (intent && intent.length > 0) {
                    setIntentToReturn(intent[0]);
                }
            } catch (error) {
                console.error("Unable to set Open Enrollment windows:\n", error);
            }
        };

        getWindows();
    }, [oeActionControls, oeDistrictWindows]);

    return (
        <div className="welcome-text-container">
            {/* Partnering with parents to select the best learning environment for every student. */}
            <h4>{welcomeTagline}</h4>
            {/* <p>
                We empower families to find the best educational fit for their children.{" "}
                <a
                    href="https://www.dcsdk12.org/schools-academics/schools/school-locator-map"
                    rel="noreferrer"
                    target="_blank"
                >
                    Learn More
                </a>
            </p> */}
            {welcomeP1}
            {/* <p>
                Families who wish to enroll their student in a school other than their neighborhood school must use this
                tool to request Open Enrollment during one of two open enrollment periods DCSD offers annually.{" "}
                <span className="highlight">
                    You do not need to use this open enrollment tool to stay at your school of <b>current enrollment</b>
                    .
                </span>
            </p> */}
            {welcomeP2}
            {/* <h5>
                <a
                    href="https://docs.google.com/document/d/1sr3ARzZGUmmJdNSjlWnLM0QWj8ipKHJb3RkwLr72_P0/edit"
                    rel="noreferrer"
                    target="_blank"
                >
                    Click here
                </a>{" "}
                for step by step instructions on how to use the tool.
            </h5> */}
            {welcomeH51}
            <br />
            {oeDistrictWindows && parentOffer && parentRequest && parentRequestRound2 ? (
                <Accordion defaultActiveKey={["0"]} alwaysOpen>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            {/* <h5>Important Dates/Times</h5> */}
                            <h5>{welcomeImportantDates}</h5>
                        </Accordion.Header>
                        <Accordion.Body>
                            <p>
                                <b>
                                    {welcomeFirstWindow}: {formatDate(parentRequest.startDate, true, true)} -{" "}
                                    {formatDate(parentRequest.endDate, true, true)}
                                </b>
                            </p>
                            <p>
                                <b>
                                    {welcomeSecondWindow}: {formatDate(parentRequestRound2.startDate, true, true)} -{" "}
                                    {formatDate(parentRequestRound2.endDate, true, true)}
                                </b>
                            </p>
                            <ul>
                                <li>
                                    {/* First round open enrollment is NOT first-come, first-served; you have until{" "}
                                    <b>{formatDate(parentRequest.endDate, true, true)}</b> to complete this process. */}
                                    {T({ key: "Welcome_ul_li1a" })}{" "}
                                    <b>{formatDate(parentRequest.endDate, true, true)}</b>{" "}
                                    {T({ key: "Welcome_ul_li1b" })}
                                </li>
                                <li>
                                    {/* For first round, You can change your open enrollment request choices at any time
                                    until <b>{formatDate(parentRequest.endDate, true, true)}.</b> */}
                                    {T({ key: "Welcome_ul_li2a" })}{" "}
                                    <b>{formatDate(parentRequest.endDate, true, true)}</b>{" "}
                                </li>
                                <li>
                                    {/* Schools will make offers to families based on space availability. */}
                                    {T({ key: "Welcome_ul_li3" })}
                                </li>
                                <li>
                                    {/* Offers will not be visible to you until after{" "}
                                    <b>{formatDate(parentOffer.startDate, true, true)}</b>. You can log back into{" "}
                                    <a href="http://guardian.dcsdk12.org/" rel="noreferrer" target="_blank">
                                        EngagED Parent Portal.
                                    </a>{" "}
                                    on or after <b>{formatDate(parentOffer.startDate, true, true)}</b> to check on any
                                    offers you may have received. */}
                                    {T({ key: "Welcome_ul_li4a" })}{" "}
                                    <b>{formatDate(parentOffer.startDate, true, true)}</b>.{" "}
                                    {T({ key: "Welcome_ul_li4b" })}{" "}
                                    <b>{formatDate(parentOffer.startDate, true, true)}</b>{" "}
                                    {T({ key: "Welcome_ul_li4c" })}
                                </li>
                                <li>
                                    {/* You have until <b>{formatDate(parentOffer.endDate, true, true)}</b> to accept an
                                    open enrollment offer from a school through the{" "}
                                    <a href="http://guardian.dcsdk12.org/" rel="noreferrer" target="_blank">
                                        EngagED Parent Portal.
                                    </a> */}
                                    {T({ key: "Welcome_ul_li5a" })} <b>{formatDate(parentOffer.endDate, true, true)}</b>{" "}
                                    {T({ key: "Welcome_ul_li5b" })}{" "}
                                </li>
                                <li>
                                    {/* If your open enrollment request is not accepted by any school, or you didn&apos;t
                                    log in and accept your offer, you will automatically be placed on the second round
                                    open enrollment waitlist, which runs from{" "} */}
                                    {T({ key: "Welcome_ul_li6a" })}{" "}
                                    <b>
                                        {formatDate(parentRequestRound2.startDate, true, true)} -{" "}
                                        {formatDate(parentRequestRound2.endDate, true, true)}
                                    </b>
                                    .
                                </li>
                            </ul>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            ) : (
                <>
                    <Accordion defaultActiveKey={["0"]} alwaysOpen>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                {/* <h5>Important Dates/Times</h5> */}
                                <h5>{T({ key: "Welcome_ImportantDates" })}</h5>
                            </Accordion.Header>
                            <Accordion.Body>
                                <p>
                                    {/* Open Enrollment process for the current school year is not yet available. */}
                                    {T({ key: "Welcome_OpenEnrollmentNotAvailable" })}
                                </p>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    <Accordion defaultActiveKey={["1"]} alwaysOpen>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                <h5>
                                    {/* Kindergarten/Preschool Information */}
                                    {T({ key: "Welcome_PreschoolInfo" })}
                                </h5>
                            </Accordion.Header>
                            <Accordion.Body>
                                <ul>
                                    <li>
                                        <b>{T({ key: "Note" })}</b>:{" "}
                                        <span className="highlight">
                                            {/* This application does not support applying to DCSD preschools. */}
                                            {T({ key: "Welcome_NoSupport" })}
                                        </span>{" "}
                                        {/* For DCSD preschools,{" "}
                                        <a
                                            href="https://www.dcsdk12.org/schools-academics/schools/preschool"
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <b>click this link</b>.
                                        </a> */}
                                        {T({ key: "Welcome_PreschoolsLink" })}
                                    </li>
                                    <li>
                                        {/* Parents wishing to enroll their three & four year olds in district preschools do
                                        not go through open enrollment. Preschool registration will take place in early
                                        January. Follow this{" "}
                                        <a
                                            href="https://www.dcsdk12.org/schools-academics/schools/kindergarten"
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <b>link</b>{" "}
                                        </a>
                                        for more information. */}
                                        {T({ key: "Welcome_PreschoolEnrollment" })}
                                    </li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    <Accordion defaultActiveKey={["2"]} alwaysOpen>
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                <h5>
                                    {/* Charter School Information */}
                                    {T({ key: "Welcome_CharterSchoolInfo" })}
                                </h5>
                            </Accordion.Header>
                            <Accordion.Body>
                                {/* <h5>The following Charter Schools will be utilizing the tool this year:</h5>
                                <a
                                    href="https://www.dcsdk12.org/schools_academics/schools/charter_schools"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <u>
                                        Please contact all other Charter schools directly to learn more about enrollment
                                        options for charter schools.
                                    </u>
                                </a>
                                <ul className="charter-school-li">
                                    <li>
                                        <i>Aspen View Academy</i>
                                    </li>
                                    <li>
                                        <i>Ben Franklin Academy</i>
                                    </li>
                                    <li>
                                        <i>Challenge to Excellence</i>
                                    </li>
                                    <li>
                                        <i>DCS Montessori Charter</i>
                                    </li>
                                    <li>
                                        {" "}
                                        <i>Leman Academy of Excellence</i>
                                    </li>
                                    <li>
                                        <i>Parker Performing Arts School</i>
                                    </li>
                                    <li>
                                        <i>SkyView Academy</i>
                                    </li>
                                    <li>
                                        <i>STEM Academy</i>
                                    </li>
                                    <li>
                                        <i>World Compass Academy</i>
                                    </li>
                                </ul> */}
                                {T({ key: "Welcome_CharterSchoolInclude" })}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    <Accordion defaultActiveKey={["3"]} alwaysOpen>
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>
                                <h5>
                                    {/* Transportation Information */}
                                    {T({ key: "Welcome_TransportationInfo" })}
                                </h5>
                            </Accordion.Header>
                            <Accordion.Body>
                                {/* Transportation for resident students granted permission to attend a school or program
                                outside their neighborhood attendance area shall be furnished by their parent/guardian. */}
                                {T({ key: "Welcome_TransportationBody" })}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </>
            )}
            <Accordion defaultActiveKey={["4"]} alwaysOpen>
                <Accordion.Item eventKey="4">
                    <Accordion.Header>
                        <h5>
                            {/* General Information */}
                            {T({ key: "Welcome_GeneralInfo" })}
                        </h5>
                    </Accordion.Header>
                    <Accordion.Body>
                        <ul>
                            <li>
                                {/* You may request that a confirmation email be sent to you after your request has been
                                submitted. */}
                                {T({ key: "Welcome_GeneralInfo_ul2_li1" })}
                            </li>
                            <li>
                                {/* You can choose to have your choices emailed to you after you have completed open
                                enrollment. */}
                                {T({ key: "Welcome_GeneralInfo_ul2_li2" })}
                            </li>
                            <li>
                                {/* Second round open enrollment is &lquot;first come first served.&rquot; Students
                                requesting open enrollment only in the second round are placed on the waitlist behind
                                those that are currently on the waitlist from first round. */}
                                {T({ key: "Welcome_GeneralInfo_ul2_li3" })}
                            </li>
                            {oeDistrictWindows && intentToReturn && (
                                <li>
                                    {/* If you are open enrolled at a neighborhood school or attend a charter but would like
                                    to return to the neighborhood school associated with your address for the next
                                    school year you must file an{" "}
                                    <a
                                        href="https://www.dcsdk12.org/about/departments/choice-programming/forms"
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        intent to return form
                                    </a>{" "}
                                    <b>by</b>
                                    */}
                                    {T({ key: "Welcome_GeneralInfo_ul2_li4" })}{" "}
                                    <b>{formatDate(intentToReturn.endDate, true, true)}</b>.
                                </li>
                            )}
                            <li>
                                {/* If you are moving within the district but would like to remain at your current school,
                                you may do so my filing an{" "}
                                <a
                                    href="https://www.dcsdk12.org/about/departments/choice-programming/forms"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    intent to remain
                                </a>{" "}
                                form at any time. */}
                                {T({ key: "Welcome_GeneralInfo_li5" })}
                            </li>
                        </ul>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
};

export default WelcomeText;
